import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import hooks from '@g4/automation-hooks';
// TODO: just as a quick fix for old boneyard hosted components
import oldTheme from '../Colors';

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_CTRL = 17;
const KEY_END = 35;
const KEY_HOME = 36;
const KEY_LEFT_ARROW = 37;
const KEY_UP_ARROW = 38;
const KEY_RIGHT_ARROW = 39;
const KEY_DOWN_ARROW = 40;

// By defaulting onChange and restricting its type, we don't need to check it exists before using it
// Because: If it's not provided we do a noop() and if it's provided but not a function the developer
// will get a useful message
export const propTypes = {
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Type of input, e.g. 'tel', 'email', etc. */
  type: PropTypes.string.isRequired,
  /** The name of the control, which is submitted with the form data */
  name: PropTypes.string,
  /**
   * A regular expression that the control's value is checked against.
   * The pattern must match the entire value, not just some subset.
   */
  pattern: PropTypes.string,
  /** The minimum value. Only valid when `type` is set to `number` */
  min: PropTypes.number,
  /** The maximum value. Only valid when `type` is set to `number` */
  max: PropTypes.number,
  /** Works with the `min` and `max` attributes to limit the increments at which the value can be set */
  step: PropTypes.number,
  /**
   * IDs of the the elements that describe the field.
   * Useful when referencing an associated tooltip.
   */
  'aria-describedby': PropTypes.string,
  /** Indicates whether the form control available for interaction */
  disabled: PropTypes.bool,
  /**
   * A hint to the user of what can be entered in the control.
   * The placeholder text must not contain carriage returns or line-feeds.
   */
  placeholder: PropTypes.string,
  /** Specifies that the input will have focus when the page loads */
  autoFocus: PropTypes.bool,
  /**
   * If the value of the type attribute is text, email, search, password, tel,
   * or url, this attribute specifies the maximum number of characters (in Unicode
   * code points) that the user can enter.
   * It can exceed the value of the size attribute.
   * If it is not specified, the user can enter an unlimited number of characters.
   */
  maxLength: PropTypes.number,
  readOnly: PropTypes.bool,
  upperCase: PropTypes.bool,
  /**
   * Hides the spin button controls for numeric input fields
   *
   * MDN Warning: This feature is non-standard and is not on a standards track.
   * Do not use it on production sites facing the Web: it will not work for every user.
   */
  hideSpinControls: PropTypes.bool,
  /** A function to execute when a key has been pressed during input */
  onKeyDown: PropTypes.func,
  /** A function that passes a reference of this component to its consumer */
  onKeyPress: PropTypes.func,
  passRef: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  /** injects the generated stylesheet */
  className: PropTypes.string,
  /** input text value */
  value: PropTypes.any,
  /** base64 enconded image with fixed pixel dimensions to appear inside the field */
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
  onClick: PropTypes.func,
};

export const defaultProps = {
  autoFocus: false,
  upperCase: false,
  hideSpinControls: false,
  onChange: Function.prototype,
  onFocus: Function.prototype,
  onBlur: Function.prototype,
  onKeyDown: Function.prototype,
  onKeyPress: Function.prototype,
  passRef: Function.prototype,
  onClick: Function.prototype,
};

const FIELD_HEIGHT = 50; // px

export const baseStyles = css`
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(4)};
  height: ${FIELD_HEIGHT}px;
  width: 100%;
  border: 1px solid #d6d6d6;
  color: ${props => oldTheme.colors.modern.input.text};
  text-overflow: ellipsis;
  background: ${props => oldTheme.colors.modern.input.background};
  border-radius: 8px;

  &:focus,
  &:hover:not(:disabled) {
    box-shadow: 0 0 0 4px ${props => props.theme.colors.hob.input.focused};
  }

  ${props =>
    props.upperCase &&
    css`
      text-transform: uppercase;
    `};

  ${props => {
    // Todo: Add validation to ensure props.image.height <= FIELD_HEIGHT
    if (props.image) {
      const spacing = `${(FIELD_HEIGHT - props.image.height) / 2}px`;

      return css`
        background-image: url('${props.image.src}');
        background-repeat: no-repeat;
        background-size: auto ${props.image.height}px;

        /* Ensure spacing is consistent in both axis */
        background-position: calc(100% - ${spacing}) 50%;

        /* Update right padding to avoid overlapping the image */
        padding-right: calc(${spacing} + ${props.image.width}px + ${props =>
        props.theme.spacing(2)});
      `;
    }
  }};

  ${props =>
    props.hideSpinControls &&
    css`
      appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        appearance: none;
        margin: 0;
      }
    `};

  &::placeholder {
    text-overflow: ellipsis;
  }

  ::-webkit-input-placeholder {
    color: ${props => props.theme.colors.hob.input.placeholder};
  }
  ::-moz-placeholder {
    opacity: 1;
    color: ${props => props.theme.colors.hob.input.placeholder};
  }
  :-ms-input-placeholder {
    color: ${props => props.theme.colors.hob.input.placeholder};
  }
`;

export class Input extends PureComponent {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.toRef = this.toRef.bind(this);
  }

  static propTypes = propTypes;
  static defaultProps = defaultProps;

  static allowedKeys = [
    KEY_BACKSPACE,
    KEY_TAB,
    KEY_ENTER,
    KEY_SHIFT,
    KEY_CTRL,
    KEY_END,
    KEY_HOME,
    KEY_LEFT_ARROW,
    KEY_UP_ARROW,
    KEY_RIGHT_ARROW,
    KEY_DOWN_ARROW,
  ];

  /** Whitelist of attributes spread on to the `input` element */
  static allowedAttrs = [
    'pattern',
    'min',
    'max',
    'type',
    'step',
    'name',
    'maxLength',
    'disabled',
    'placeholder',
    'readOnly',
    'aria-describedby',
    'autoComplete',
  ];

  componentDidUpdate(prevProps) {
    // Alternative to native 'autofocus' html attr which is not supported on Safari
    if (this.props.autoFocus && prevProps.autoFocus !== this.props.autoFocus) {
      this.el.focus();
    }
  }

  handleChange(event) {
    this.props.onChange(event.target.value);
  }

  handleBlur(event) {
    this.props.onBlur(event.target.value);
  }

  onKeyDown(event) {
    // GMOB-693: Prevent key press when the max length has been reached.
    // This is because some input types (for example 'number') don't support the 'maxlength' attr.

    if (!(this.constructor.allowedKeys.indexOf(event.keyCode) !== -1)) {
      if (
        this.props.maxLength &&
        this.value.toString().length >= this.props.maxLength
      ) {
        event.preventDefault();
      }
    }
  }

  toRef(el) {
    this.el = el;
    this.props.passRef && this.props.passRef(el);
  }

  get value() {
    return this.props.value !== undefined ? this.props.value : '';
  }

  get attrs() {
    return _.pick(this.props, this.constructor.allowedAttrs);
  }

  render() {
    var htmlAttrs = { ...this.attrs };

    if (htmlAttrs.maxLength) {
      htmlAttrs.onKeyDown = this.onKeyDown;

      // Attribute `maxlength` is only allowed when the input type is `email`, `password`, `search`, `tel`, `text`, or `url`
      if (
        ['email', 'password', 'search', 'tel', 'text', 'url'].indexOf(
          this.props.type
        ) === -1
      ) {
        delete htmlAttrs.maxLength;
      }
    }

    return (
      <input
        {...htmlAttrs}
        ref={this.toRef}
        className={this.props.className}
        value={this.value}
        onKeyPress={this.props.onKeyPress}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        onClick={this.props.onClick}
        onFocus={this.props.onFocus}
        {...hooks.hook(this.props.automationHook)}
      />
    );
  }
}

export default styled(Input)`
  ${baseStyles};
`;